import { ColumnDef } from '@tanstack/react-table';
import { Trash } from '@phosphor-icons/react';
import { DataTableColumnHeader, StatusChip, UserNameCell } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { formatOrgDateTime } from '@/lib/utils/custom-date/custom-date';
import { SurveySubmission } from '../../types/survey.types';

const STATUSES = ['Pending', 'Completed'] as const;

interface CreateSubmissionsTableColumnsProps {
  t: (key: string) => string;
  onView: (submission: SurveySubmission) => void;
  onDelete: (submission: SurveySubmission) => void;
}

export const createSubmissionsTableColumns = ({
  t,
  onView,
  onDelete,
}: CreateSubmissionsTableColumnsProps): ColumnDef<SurveySubmission>[] => [
  {
    id: 'UserName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.UserName ?? '',
    cell: ({ row }) => <UserNameCell name={row.original.UserName} email={row.original.UserEmail} />,
  },
  {
    id: 'Status',
    meta: {
      filter: {
        type: 'options',
        options: STATUSES.map((status) => ({ value: status, label: t(status.toUpperCase()) })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.Status,
    cell: ({ row }) => (
      <StatusChip status={row.original.Status}>{t(row.original.Status.toUpperCase())}</StatusChip>
    ),
  },
  {
    id: 'SubmittedDateUtc',
    meta: { filter: { type: 'datetime' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('SUBMITTED_DATE')} />,
    accessorFn: (row) => row.SubmittedDateUtc,
    cell: ({ row }) =>
      formatOrgDateTime(row.original.SubmittedDateUtc, row.original.Timezone || undefined) || '-',
  },
  {
    id: 'actions',
    // Card: top right, as the old portal's card header.
    meta: { mobile: 'badge' as const },
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        {row.original.Status === 'Completed' && (
          <Button variant="outline" size="sm" onClick={() => onView(row.original)}>
            {t('VIEW')}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(row.original)}
          aria-label={t('DELETE')}
        >
          <Trash className="size-4" />
        </Button>
      </div>
    ),
  },
];
